// 3rd party
import React from 'react';
import { Options } from 'highcharts';

// Components
import { GrowthChart, DrawdownAnalysis, RiskRewardPlot } from '@/views/components/charts';

// Utils
import { getGrowthChartState, getDrawdownChartState, getRiskRewardChartState } from '../utils/chart-container-helpers';
import { getRollingIntervalFromDates, getExpandedPortfolioItems, mergeChartItems } from '@/utils/charts';
import { calculateRiskRewardAxes, formatRiskRewardItems, getRiskRewardChartOptions } from '../utils/risk-reward-chart-helpers';
import { getChartOptions } from '../utils/drawdown-chart-helpers';
import { LOCALE } from '@/utils/i18n';

// Types
import { ScatterPlotDatum, ScatterPlotOptions } from '@/views/components/charts/scatter-plot/ScatterPlot';
import Comparison from '@/types/comparison';
import ComparisonItem from '@/types/comparison-item';
import Locale from '@/types/locale';
import User from '@/types/user';
import { ExpandedPortfoliosData } from '../types';

export const renderGrowthChart = (
  user: User,
  comparison: Comparison,
  items: ComparisonItem[],
  dark: boolean,
  chartOptions: Options,
  locale: Locale = LOCALE.EN_CA,
) => {
  const growthChart = getGrowthChartState(user, comparison);
  const interval = getRollingIntervalFromDates(
    growthChart.startDate,
    growthChart.endDate,
    items,
    comparison.options?.growth?.useMonthly ?? false,
    comparison.shortHistory,
  );

  return (
    <GrowthChart
      chartOptions={chartOptions}
      items={items}
      dark={dark}
      showBaseCurrencies={comparison.showBaseCurrencies}
      targetName={comparison.options?.growth?.targetName}
      targetROI={growthChart.targetROI}
      dateQuickRange={growthChart.dateQuickRange}
      interval={interval}
      locale={locale}
      shortHistory={comparison.shortHistory}
      startDate={growthChart.startDate}
      endDate={growthChart.endDate}
    />
  );
};

export const renderRiskRewardChart = (
  user: User,
  comparison: Comparison,
  items: ComparisonItem[],
  dark: boolean,
  expandedPortfoliosData: ExpandedPortfoliosData,
) => {
  const riskRewardChart = getRiskRewardChartState(user, comparison);
  const portfolioHoldingItems = getExpandedPortfolioItems(items, user.appConfig.appRegion, expandedPortfoliosData);
  const chartItems = mergeChartItems(items, portfolioHoldingItems);
  const data: ScatterPlotDatum[] = formatRiskRewardItems(
    chartItems,
    riskRewardChart.monthsRange,
    comparison.showBaseCurrencies ?? false,
    user.appConfig.localCurrency,
  );

  const axes = calculateRiskRewardAxes(data, 600, 400);
  const chartOptions: ScatterPlotOptions = getRiskRewardChartOptions(axes);

  return <RiskRewardPlot items={items} data={data} dark={dark} chartOptions={chartOptions} />;
};

export const renderDrawdownChart = (
  user: User,
  comparison: Comparison,
  items: ComparisonItem[],
  dark: boolean,
  expandedPortfoliosData: ExpandedPortfoliosData,
) => {
  const drawdownChart = getDrawdownChartState(user, comparison);
  const interval = getRollingIntervalFromDates(
    drawdownChart.startDate,
    drawdownChart.endDate,
    items,
    drawdownChart.useMonthly ?? false,
    comparison.shortHistory,
  );

  return (
    <DrawdownAnalysis
      fillArea
      items={items}
      localCurrency={user.appConfig.localCurrency}
      dark={dark}
      expandedPortfoliosData={expandedPortfoliosData}
      showBaseCurrencies={comparison.showBaseCurrencies}
      shortHistory={comparison.shortHistory}
      chartOptions={getChartOptions()}
      startDate={drawdownChart.startDate}
      endDate={drawdownChart.endDate}
      useMonthly={drawdownChart.useMonthly ?? false}
      appRegion={user.appConfig.appRegion}
      interval={interval}
      dateQuickRange={drawdownChart.dateQuickRange}
    />
  );
};
